<script module lang="ts">
  export const Blur = 0.2;

  export enum BGMode {
    SINGLE = 'single',
    DOUBLE = 'double',
    MULTI = 'multi',
    INITIAL = 'initial'
  }

  export interface SectionConfig {
    element: HTMLElement;
    mode: BGMode;
    start: number;
    end: number;
    color1?: string;
    color2?: string;
  }

  //This maps the sections read from the page to Uniform sections
  //colors are converted form hex to array of numbers
  export interface BGSection {
    start: number;
    end: number;
    color: number[];
  }

  const hexToVecArray = (hex = '') => {
    if (hex == '') {
      hex = '#FFF';
    }
    const { red, green, blue } = hexRgb(hex);
    return [red / 255, green / 255, blue / 255];
  };

  export const getBGSections = (sectionConfig: SectionConfig[]) => {
    const bgSections: BGSection[] = [];

    sectionConfig.forEach((config) => {
      // set color depending on mode
      const sectionHeight = config.end - config.start;
      const sectionBlur = Math.round(Blur * sectionHeight);
      const solidItemConfig = sectionConfig.find((item) =>
        [BGMode.SINGLE, BGMode.DOUBLE].includes(item.mode)
      );

      switch (config.mode) {
        case BGMode.INITIAL:
          //initial always matches either the first solid color after the header
          if (solidItemConfig) {
            bgSections.push({
              color: hexToVecArray(solidItemConfig.color1),
              start: config.start + sectionBlur,
              end: config.end - sectionBlur
            });
          }

          break;
        case BGMode.MULTI:
          //using -1,-1,-1 as a flag to the shader that this is not a solid color since that would be an impossible hex RGB value
          bgSections.push(<BGSection>{
            color: [-1, -1, -1],
            start: config.start + sectionBlur,
            end: config.end - sectionBlur
          });
          break;
        case BGMode.DOUBLE:
          //do twice using the 2 colors
          // eslint-disable-next-line no-case-declarations
          const halfHeight = (config.end - config.start) / 2;
          bgSections.push(<BGSection>{
            color: hexToVecArray(config.color1),
            start: config.start + sectionBlur,
            end: config.start + halfHeight - sectionBlur
          });
          bgSections.push(<BGSection>{
            color: hexToVecArray(config.color2),
            start: config.end - halfHeight + sectionBlur,
            end: config.end - sectionBlur
          });
          break;
        case BGMode.SINGLE:
        default:
          bgSections.push(<BGSection>{
            color: hexToVecArray(config.color1),
            start: config.start + sectionBlur,
            end: config.end - sectionBlur
          });
          break;
      }
    });
    return bgSections;
  };

  // For 'multi' mode (aka calico) header section:
  // This creates a config object for the 'points' which represent the
  // center and color of the blobs, the colors are picked from all sections

  export interface BGHeaderPoint {
    color: number[];
    position: number[];
    seed: number[];
  }

  const getRandomSeed = () => {
    return Array.from({ length: 60 }, Math.random);
  };

  //helper function takes hex string and returns array of numbers [%red, %green, %blue]
  const getBGColors = (sectionConfig: SectionConfig[]): string[] => {
    const color1 = sectionConfig.map((config) => config.color1);
    const color2 = sectionConfig.map((config) => config.color2);
    // return unique colors only and no undefined
    return <string[]>[...new Set([...color1, ...color2])].filter((color) => color != undefined);
  };

  export const getBGHeaderPoints = (sectionConfig: SectionConfig[]) => {
    const bgPoints: BGHeaderPoint[] = [];
    for (let i = 0; i < 15; i++) {
      const bgColors = getBGColors(sectionConfig);
      const randomSeed = getRandomSeed();
      bgPoints.push(<BGHeaderPoint>{
        color: hexToVecArray(bgColors[i % bgColors.length]),
        position: randomSeed.slice(i * 4, i * 4 + 2),
        seed: randomSeed.slice(i * 4 + 2, i * 4 + 4)
      });
    }
    return bgPoints;
  };
</script>

<script lang="ts">
  import { run } from 'svelte/legacy';

  import { browser } from '$app/environment';
  import { onDestroy, type Snippet } from 'svelte';
  import { TransitionStatus } from '$components/Transitions/PageTransitionStore';
  import { accessibilityPreferences } from '$lib/stores/accessibility';
  import debounce from 'lodash/debounce';
  import WebGlBackground from './WebGLBackground.svelte';
  import CssBackground from './CSSBackground.svelte';
  import hexRgb from 'hex-rgb';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  interface Props {
    children?: Snippet;
  }

  let { children }: Props = $props();

  let isMobile = $state(false);
  let wrapper: HTMLElement | undefined = $state();
  let pageHeight = $state(1);
  let sectionConfig: SectionConfig[] = [];
  let resizeObserver: ResizeObserver | undefined = $state();

  //when the height changes because elements were lazy loaded, or the page was resized
  const onResize = debounce(() => {
    if (!browser || !wrapper) return;
    if (!$TransitionStatus.isTransitioning) {
      //height changed
      let yReadPosition = 0;
      sectionConfig.forEach((config) => {
        config.start = yReadPosition;
        yReadPosition += config.element.offsetHeight;
        config.end = yReadPosition;
      });
      pageHeight = yReadPosition;

      //update mobile width
      isMobile = wrapper.offsetWidth < 980;
    }
  }, 100);

  const readSectionConfig = () => {
    if (!browser || !wrapper) return;

    const sections = wrapper.querySelectorAll('[data-background]');
    let yReadPosition = 0;

    [...sections].forEach((e) => {
      const element = e as HTMLElement;
      sectionConfig.push(<SectionConfig>{
        element,
        mode: element.dataset.bgmode,
        start: yReadPosition,
        end: yReadPosition + element.offsetHeight,
        color1: element.dataset.bgcolor1 || undefined,
        color2: element.dataset.bgcolor2 || undefined
      });
      yReadPosition += element.offsetHeight;
    });

    //updateStore
    pageHeight = yReadPosition;
  };

  onDestroy(() => {
    if (wrapper) {
      resizeObserver?.unobserve(wrapper);
    }
  });

  let brandReskinEnabled = $derived(flagValue(FEATURE_FLAGS.BRAND_RESKIN));

  run(() => {
    if (wrapper && !brandReskinEnabled) {
      // start observing a DOM node
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(wrapper);
    }
  });

  run(() => {
    if (!$accessibilityPreferences.reduceColor && !brandReskinEnabled) {
      if (wrapper) {
        const timeout =
          document.querySelectorAll('.backgroundGradientWrapper').length == 1 ||
          $accessibilityPreferences.reduceMotion
            ? 1
            : 450;

        //if there's only 1 background or reduce motion is turned on init immediately
        //if there are 2 backgrounds delay 450 for the "crossover"
        setTimeout(() => {
          readSectionConfig();
        }, timeout);
      }
    }
  });
</script>

{#if !$accessibilityPreferences.reduceColor && !brandReskinEnabled}
  <div class="backgroundGradientWrapper" bind:this={wrapper}>
    {#if $accessibilityPreferences.reduceMotion || isMobile}
      <CssBackground {pageHeight} {sectionConfig} />
    {:else}
      <WebGlBackground {pageHeight} {sectionConfig} />
    {/if}
    {@render children?.()}
  </div>
{:else}
  {@render children?.()}
{/if}
